import "quill/dist/quill.snow.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useIntrospectionData } from "../../components/Hooks/useIntrospectionData.jsx";
import { usePrompt } from "../../components/Hooks/usePrompt.jsx";
import LoaderModal from "../../components/LoaderModal/LoaderModal.jsx";
import { useUserState } from "../../context/UserContext.js";
import { useAuthentication } from "../hooks/useAuthentication.js";
import { getMyMentor } from "../pojediniProgrami/SectionContainer/API.js";
import { fetchData } from "../pojediniProgrami/SectionContainer/utils.js";
import styles from "./Introspekcije.module.css";
import MentorComment from "./components/MentorComment/MentorComment.jsx";
import Navigation from "./components/Polaznik/Navigation.jsx";
import PolaznikView from "./components/Polaznik/PolaznikView.jsx";
import RequestHandlingModals from "./components/RequestHandlingModals.jsx";
import VoditeljView from "./components/Voditelj/VoditeljView.jsx";
import {
  handleNotify,
  handleSaveClick,
  handleUpdate,
} from "./utils/handlers.js";
import { FIRST_IDS, getIntrospectionNumber, LAST_IDS } from "./utils/utils.js";

function IntrospekcijeContainer({
  comments,
  answers,
  handleCommentsUpdate,
  isMentorRole,
  polaznikId,
  groupProgramId,
  introspectionIdMentor,
  introspectionQuestions,
  isCommentSent,
  setIsCommentSent,
  setIsCommentTrue,
  isCommentTrue,
  polaznikEmail,
}) {
  const quillRef = useRef();
  const { introspectionId, programId, title } = useIntrospectionData(
    isMentorRole,
    groupProgramId,
    introspectionIdMentor
  );

  const [isEditorDirty, setIsEditorDirty] = useState(false);
  const [isCommentDirty, setIsCommentDirty] = useState(false);
  usePrompt(
    "You have unsaved changes. Are you sure you want to leave?",
    quillRef.current?.isDirty?.() === true || isCommentDirty
  );
  const location = useLocation();
  const { role, userData } = useUserState();
  const { authToken } = useAuthentication();
  const initialRoute = useRef(location?.state?.previousRoute);
  const firstIds = React.useMemo(() => FIRST_IDS, []);
  const lastIds = React.useMemo(() => LAST_IDS, []);
  const userId = role;
  const minCommentSize = 150;
  let introspectionNumber = getIntrospectionNumber(programId, introspectionId);
  const [showEditComment, setShowEditComment] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [introspectionData, setIntrospectionData] = useState([]);
  const [disabledNext, setDisabledNext] = useState(false);
  const [disabledPrevious, setDisabledPrevious] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [isNotify, setIsNotify] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [userAnswer, setUserAnswer] = useState(answers || "");
  const [commentSize, setCommentSize] = useState(0);
  const [isCommentDone, setIsCommentDone] = useState(false);
  const [isReal, setIsReal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMaxCharCount, setIsMaxCharCount] = useState(false);
  const [dbComment, setDbComment] = useState("");
  const [mentorData, setMentorData] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isQuillInitialized, setIsQuillInitialized] = useState(true);
  const [isNoCharCount, setIsNoCharCount] = useState(false);
  const [isNoCharComment, setIsNoCharComment] = useState(false);
  const programTitle = useMemo(() => {
    return title || "";
  }, [title]);
  useEffect(() => {
    if (authToken && !isMentorRole) {
      fetchData(
        introspectionId,
        authToken,
        setIntrospectionData,
        setIsReal,
        setDbComment,
        setIsLoading
      );
    }
    if (inputValue !== undefined) {
      let commentLength = inputValue
        ?.replace(/[.,/#!$%^&*;:{}=\-_`~()?]/g, "")
        .replace(/\s|&nbsp;/g, "").length;

      if (commentLength < minCommentSize) {
        setCommentSize(commentLength);
        setIsCommentDone(false);
      } else {
        setCommentSize(minCommentSize);
        setIsCommentDone(true);
      }
    }
    const fetchMentor = async () => {
      if (!isMentorRole) {
        const response = await getMyMentor(userData?.id, programId, authToken);
        setMentorData(response?.mentor);
        let group = response?.userGroup.find(
          (group) => group?.mentorId === response?.mentor?.id
        );
        setSelectedGroup(group?.groupId);
      }
    };
    if (authToken) {
      fetchMentor();
    }
  }, [
    authToken,
    introspectionId,
    isMentorRole,
    inputValue,
    programId,
    userData.id,
    comments,
  ]);
  useEffect(() => {
    if (!isCommentDirty) {
      if (dbComment) {
        setInputValue(dbComment);
      } else if (comments) {
        setInputValue(comments);
      }
    }
  }, [dbComment, comments, isCommentDirty]);

  const handleInputChange = (event) => {
    setIsCommentDirty(true);
    setInputValue(event.target.value);
  };
  useEffect(() => {
    if (!inputValue) {
      setIsNoCharComment(true);
    } else {
      setIsNoCharComment(false);
    }
  }, [setIsNoCharComment, inputValue, isNoCharComment]);
  return (
    <div className={styles.introspectionWrapper}>
      {isLoading ? (
        <LoaderModal />
      ) : !introspectionData.id && !isMentorRole ? (
        <>
          {" "}
          <Navigation
            initialRoute={initialRoute}
            title={programTitle}
            disabledPrevious={disabledPrevious}
            introspectionId={introspectionId}
            programId={programId}
            introspectionNumber={introspectionNumber}
            disabledNext={disabledNext}
          />
          <div className={styles.emptyContainer}>
            Ova introspekcija trenutno nije dostupna
          </div>
        </>
      ) : (
        <>
          <RequestHandlingModals
            isModalOpen={isModalOpen}
            isMaxCharCount={isMaxCharCount}
            isDone={isDone}
            isMentorRole={isMentorRole}
            setIsModalOpen={setIsModalOpen}
            isNotifyModalOpen={isNotifyModalOpen}
            setIsNotifyModalOpen={setIsNotifyModalOpen}
            isErrorModalOpen={isErrorModalOpen}
            isNotify={isNotify}
            setIsErrorModalOpen={setIsErrorModalOpen}
            setIsNotify={setIsNotify}
            setIsCommentSent={setIsCommentSent}
            isCommentSent={isCommentSent}
            isCommentTrue={isCommentTrue}
            setIsCommentTrue={setIsCommentTrue}
          />
          {isMentorRole ? (
            <VoditeljView
              answers={answers}
              introspectionQuestions={introspectionQuestions}
              userId={userId}
              isMentorRole={isMentorRole}
            />
          ) : (
            <PolaznikView
              initialRoute={initialRoute}
              title={title}
              firstIds={firstIds}
              introspectionId={introspectionId}
              setDisabledPrevious={setDisabledPrevious}
              lastIds={lastIds}
              setDisabledNext={setDisabledNext}
              disabledPrevious={disabledPrevious}
              disabledNext={disabledNext}
              programId={programId}
              setIsLoading={setIsLoading}
              isQuillInitialized={isQuillInitialized}
              setIsQuillInitialized={setIsQuillInitialized}
              introspectionNumber={introspectionNumber}
              isDone={isDone}
              handleUpdate={async () => {
                await handleUpdate({
                  introspectionId,
                  dbComment,
                  comments,
                  userAnswer,
                  isMentorRole,
                  polaznikId,
                  userData,
                  isDone,
                  isReal,
                  authToken,
                  setIsErrorModalOpen,
                  setIsReal,
                  setIntrospectionData,
                  setDbComment,
                  setIsLoading,
                });
                setIsEditorDirty(false);
                setIsCommentDirty(false);
              }}
              setIsModalOpen={setIsModalOpen}
              mentorData={mentorData}
              introspectionData={introspectionData}
              handleNotify={async () => {
                await handleNotify({
                  isMentorRole,
                  polaznikId,
                  mentorData,
                  userData,
                  introspectionNumber,
                  setIsLoading,
                  authToken,
                  introspectionData,
                  introspectionId,
                  setIntrospectionData,
                  setIsReal,
                  setDbComment,
                  setIsNotifyModalOpen,
                  setIsNotify,
                  setIsErrorModalOpen,
                  selectedGroup,
                  title,
                });
                setIsEditorDirty(false);
                setIsCommentDirty(false);
              }}
              answers={answers}
              setUserAnswer={setUserAnswer}
              userId={userId}
              isNoCharCount={isNoCharCount}
              setIsNoCharCount={setIsNoCharCount}
              setIsMaxCharCount={setIsMaxCharCount}
              setIsDone={setIsDone}
              isMentorRole={isMentorRole}
              isMaxCharCount={isMaxCharCount}
              setIntrospectionData={setIntrospectionData}
              setIsEditorDirty={setIsEditorDirty}
              quillRef={quillRef}
              isEditorDirty={isEditorDirty}
            />
          )}
          {((isMentorRole && answers.length > 0) || !isMentorRole) && (
            <MentorComment
              isMentorRole={isMentorRole}
              userId={userId}
              showEditComment={showEditComment}
              comments={comments}
              userData={userData}
              mentorData={mentorData}
              dbComment={dbComment}
              handleInputChange={handleInputChange}
              setShowEditComment={setShowEditComment}
              introspectionData={introspectionData}
              inputValue={inputValue}
              setIsNoCharComment={setIsNoCharComment}
              minCommentSize={minCommentSize}
              commentSize={commentSize}
            />
          )}
          {showEditComment && (
            <div className={styles.editModeButtonscontainer}>
              <div
                className={styles.cancelButton}
                onClick={() => {
                  setShowEditComment(false);
                  setInputValue(comments || "");
                  setIsEditorDirty(false);
                  setIsCommentDirty(false);
                }}
              >
                <div className={styles.cancelButtonText}>Odustani </div>
              </div>
              <button
                className={styles.saveBtn}
                disabled={isNoCharComment}
                onClick={async () => {
                  handleSaveClick({
                    setIsLoading,
                    inputValue,
                    minCommentSize,
                    handleCommentsUpdate,
                    isCommentDone,
                    isMentorRole,
                    polaznikId,
                    mentorData,
                    userData,
                    introspectionNumber,
                    authToken,
                    introspectionData,
                    introspectionId,
                    setIntrospectionData,
                    setIsReal,
                    setDbComment,
                    setIsNotifyModalOpen,
                    setIsNotify,
                    setIsErrorModalOpen,
                    setShowEditComment,
                    setInputValue,
                    comments,
                    selectedGroup,
                    title,
                    emailAddressPolaznik: polaznikEmail,
                  });
                  setIsEditorDirty(false);
                  setIsCommentDirty(false);
                }}
              >
                Spremi komentar
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default IntrospekcijeContainer;
